import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Button } from "react-bootstrap";
import { toast } from "react-toastify";
import { saveSmartConnectSettings } from "../../../store/slices/smartConnect/smartConnectSettings";
import { hasPermission } from "../../../services/utils";
import { PERMISSIONS } from "../../../contants/permission";

function SmartConnectSettingForm({ setting, onUpdated }) {
  const dispatch = useDispatch();
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [form, setForm] = useState({
    settingName: "",
    didsPerAgent: "",
    daysToSpam: "",
    daysToShuffle: "",
    daysToAssignAdditionalDid: "",
  });

  useEffect(() => {
    if (!isEditMode) {
      setForm({
        settingName: setting.setting_name || "",
        didsPerAgent: String(setting.dids_per_agent ?? ""),
        daysToSpam: String(setting.days_to_spam ?? ""),
        daysToShuffle: String(setting.days_to_shuffle ?? ""),
        daysToAssignAdditionalDid: String(
          setting.days_to_assign_additional_did ?? "",
        ),
      });
    }
  }, [setting, isEditMode]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name !== "settingName") {
      if (!/^\d*$/.test(value)) return;
    }

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSaving) return;

    try {
      setIsSaving(true);

      const user = JSON.parse(localStorage.getItem("user"));
      const userId = user?.data?.id;

      if (!userId) {
        toast.error("Invalid session. Please login again.");
        return;
      }

      await dispatch(
        saveSmartConnectSettings({
          id: setting.id,
          setting_name: form.settingName.trim(),
          dids_per_agent: Number(form.didsPerAgent),
          days_to_spam: Number(form.daysToSpam),
          days_to_shuffle: Number(form.daysToShuffle),
          days_to_assign_additional_did: Number(form.daysToAssignAdditionalDid),
          updated_by: userId,
        }),
      ).unwrap();

      toast.success("Settings updated successfully");
      setIsEditMode(false);
      onUpdated?.();
    } catch (err) {
      toast.error(err || err?.message || "Failed to save settings");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="border rounded p-3 bg-light">
      <div className="mb-3">
        <label>Group Name</label>
        <input
          type="text"
          name="settingName"
          className="form-control"
          value={form.settingName}
          onChange={handleChange}
          disabled={!isEditMode || isSaving}
        />
      </div>

      <div className="row mb-2">
        <div className="col-md-3">
          <label>DIDs Per Agent</label>
          <input
            inputMode="numeric"
            name="didsPerAgent"
            className="form-control"
            value={form.didsPerAgent}
            onChange={handleChange}
            disabled={!isEditMode || isSaving}
          />
        </div>

        <div className="col-md-3">
          <label>Days to Spam</label>
          <input
            inputMode="numeric"
            name="daysToSpam"
            className="form-control"
            value={form.daysToSpam}
            onChange={handleChange}
            disabled={!isEditMode || isSaving}
          />
        </div>

        <div className="col-md-3">
          <label>Days to Shuffle</label>
          <input
            inputMode="numeric"
            name="daysToShuffle"
            className="form-control"
            value={form.daysToShuffle}
            onChange={handleChange}
            disabled={!isEditMode || isSaving}
          />
        </div>

        <div className="col-md-3">
          <label>Days to Assign Additional DID</label>
          <input
            inputMode="numeric"
            name="daysToAssignAdditionalDid"
            className="form-control"
            value={form.daysToAssignAdditionalDid}
            onChange={handleChange}
            disabled={!isEditMode || isSaving}
          />
        </div>
      </div>

      <div className="text-muted small mb-3">
        Note: Enter <strong>0</strong> to disable a rule (no rule will be
        applied).
      </div>

      {hasPermission(PERMISSIONS.SMART_CONNECT.EDIT) && (
        <div className="d-flex justify-content-end gap-2">
          {!isEditMode && hasPermission(PERMISSIONS.SMART_CONNECT.EDIT) ? (
            <Button
              variant="secondary"
              type="button"
              onClick={() => setIsEditMode(true)}
            >
              Edit
            </Button>
          ) : (
            <>
              <Button
                variant="outline-secondary"
                type="button"
                onClick={() => {
                  setForm({
                    settingName: setting.setting_name || "",
                    didsPerAgent: String(setting.dids_per_agent ?? ""),
                    daysToSpam: String(setting.days_to_spam ?? ""),
                    daysToShuffle: String(setting.days_to_shuffle ?? ""),
                    daysToAssignAdditionalDid: String(
                      setting.days_to_assign_additional_did ?? "",
                    ),
                  });
                  setIsEditMode(false);
                }}
              >
                Cancel
              </Button>

              {hasPermission(PERMISSIONS.SMART_CONNECT.EDIT) && (
                <Button variant="primary" type="submit" disabled={isSaving}>
                  {isSaving ? "Saving..." : "Save"}
                </Button>
              )}
            </>
          )}
        </div>
      )}
    </form>
  );
}

export default SmartConnectSettingForm;
